import React, { useState } from "react";
import { useLocation } from 'react-router';
import * as CaptchaLib from "@repoflow/browser/captcha";
import { useCaptcha } from "@repoflow/components/captcha";
import AuthSchema from "@common/schema/auth";
import { Link } from "react-router-dom";
import * as Loading from "@repoflow/components/loading";
import { Component as Form } from "@repoflow/form-schema";
import { Container, Col, Row, Alert } from "reactstrap";
import { basename } from "env";
import { gql } from "@apollo/client";
import * as Section from "@repoflow/components/section";
import { QueryContent } from "@repoflow/components/content-query-anonymous";
import { MissingDomain } from "routes/auth/login";
import * as AuthQL from "queries/auth";

const REGISTER = gql`
  mutation Register(
    $email: String!
    $domainId: String!
    $token: String!
  ) {
    viewer {
      anonymous(token: $token) {
        auth {
          register(email: $email, domainId: $domainId) 
        }
      }
    }
  }
`;

export default function () {
  const location = useLocation();
  const domainId = new URLSearchParams(location.search).get("domainId");

  if (!domainId) {
    return <MissingDomain />;
  }

  return (
    <QueryContent query={AuthQL.session} variables={{ domainId }}>
      {ContentHandler}
    </QueryContent>
  );
}


const ContentHandler = ({ anonymous, variables: { domainId }, error }) => {
  const session = anonymous.auth.session;
  const [phase, setPhase] = useState("init");
  const { publicKey, ready } = useCaptcha();

  if (!ready) {
    return <Loading.Content />;
  }

  // AnonymousAuthSession is nullable: an unknown domainId resolves to null and
  // reading session.description off it took the page down with it.
  if (!session) {
    return <MissingDomain />;
  }

  return (
    <Section.Provider schema={AuthSchema}>
      <Section.Mutation
        guard={false}
        context={{ session, domainId, phase }}
        initial={{ email: "", terms: false }}
        mutation={REGISTER}
        schema={AuthSchema}
        target={{ $ref: "/Input/Register/UI" }}
        onMutate={({ handler, state: { values } }) => {
          const { email } = values;

          setPhase("init");
          CaptchaLib.request(publicKey, (token) => {
            handler({
              variables: {
                email,
                domainId,
                token,
              },
            });
          });
        }}
        onError={(error) => {
          setPhase("error");
        }}
        onCompleted={({
          viewer: {
            anonymous: {
              auth: { register },
            },
          },
        }) => {
          if (register) {
            window.location = `${basename}/auth/validate?domain=${encodeURIComponent(session.domain)}`;
          }
        }}
      >
        {MutationHandler}
      </Section.Mutation>
    </Section.Provider>
  );
};

const MutationHandler = ({
  schema,
  target,
  state: { values, isValid, setValues },
  error,
  context: { session, domainId, phase }
}) => {
  const formProps = {
    schema,
    target,
    values,
    onChange: (values) => {
      setValues(values);
    },
    config: {
      "/Input/Register/UI": {
        fields: [
          {
            label: "E-mail",
            property: "email",
          },
          {
            label: (
              <span>
                I have read and I accept the{" "}
                <a href={"/terms"}>Terms and conditions</a> and{" "}
                <a href={"/privacy"}>Privacy notice</a>
              </span>
            ),
            property: "terms",
          },
        ],
      },
    },
  };

  return (
    <>
      <Container>

        <Row>
          <Col className="text-center">
            <div className="mt-4"></div>
            Sign up to <h1><b>{session.description}</b></h1>
          </Col>
        </Row>
        <Row>
          <Col>
            <div className="mt-4"></div>
            <Form {...formProps} >
              <Section.Button type={"submit"}>Sign Up</Section.Button>
            </Form>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col>
            {phase === "error" && (
              <Alert color="danger">
                The user already exists or there is an issue with the provided
                email
              </Alert>
            )}
          </Col>
        </Row>

        <Row>
          <Col className="text-center">
            <Link to={`/auth/login?domainId=${encodeURIComponent(domainId)}`}>Sign in</Link> if you already have an account
          </Col>
        </Row>
      </Container>
    </>
  );
};
